import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Layout } from '../components/Layout';

export const SetupExtraSecurityLayers = () => {
  const navigate = useNavigate();

  const [googleAuth, setGoogleAuth] = useState(false);
  const [emailAuth, setEmailAuth] = useState(false);
  const [smsAuth, setSmsAuth] = useState(false);

  useEffect(() => {
    setGoogleAuth(Cookies.get('authentication_level_google_auth') === 'pass');
    setEmailAuth(Cookies.get('authentication_level_email') === 'pass');
    setSmsAuth(Cookies.get('authentication_level_sms') === 'pass');
  }, []);

  const cardStyle = `rounded-2xl p-4 border border-white/10 transition-all relative`;

  const SetupStatus = ({ isComplete }) => (
    <div className="mt-2 text-xs text-start text-white/50 italic">
      {isComplete ? 'Setup complete' : 'Not yet set up'}
    </div>
  );

  const ActiveArrow = () => (
    <div className="absolute top-4 right-4 animate-bounce text-white/70">
      <ArrowRight size={18} />
    </div>
  );

  return (
    <Layout title="Secure Your Wire24 Account">
      <div className="text-white text-lg font-semibold mb-2">
        Add extra protection to your account
      </div>
      <p className="text-white/80 text-sm mb-6">
        Before you can access your account, please activate the following
        security layers. This helps ensure your account is only accessible to
        you.
      </p>

      <div className="grid grid-cols-1 gap-4">
        {/* Google Authenticator */}
        <button
          onClick={() => navigate('/auth/security/authenticator/setup')}
          className={`${cardStyle} ${
            googleAuth ? 'bg-green-500/10' : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          {!googleAuth && <ActiveArrow />}
          <div className="flex items-center justify-between mb-1">
            <div className="text-white font-medium">Google Authenticator</div>
            {googleAuth && <CheckCircle className="text-green-400" size={18} />}
          </div>
          <div className="text-white/70 text-sm text-start">
            Protect your account with time-based verification from your mobile
            device.
          </div>
          <SetupStatus isComplete={googleAuth} />
        </button>

        {/* Email Verification */}
        <button
          onClick={() => googleAuth && navigate('/auth/security/email/setup')}
          disabled={!googleAuth}
          className={`${cardStyle} ${
            emailAuth
              ? 'bg-green-500/10'
              : !googleAuth
              ? 'bg-white/5 cursor-not-allowed opacity-50'
              : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          {!emailAuth && googleAuth && <ActiveArrow />}
          <div className="flex items-center justify-between mb-1">
            <div className="text-white font-medium">Email Verification</div>
            {emailAuth && <CheckCircle className="text-green-400" size={18} />}
          </div>
          <div className="text-white/70 text-sm text-start">
            A confirmation code will be sent to your email for additional
            security.
          </div>
          <SetupStatus isComplete={emailAuth} />
        </button>

        {/* SMS Verification */}
        <button
          onClick={() => emailAuth && navigate('/auth/security/sms/setup')}
          disabled={!emailAuth}
          className={`${cardStyle} ${
            smsAuth
              ? 'bg-green-500/10'
              : !emailAuth
              ? 'bg-white/5 cursor-not-allowed opacity-50'
              : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          {!smsAuth && emailAuth && <ActiveArrow />}
          <div className="flex items-center justify-between mb-1">
            <div className="text-white font-medium">SMS Verification</div>
            {smsAuth && <CheckCircle className="text-green-400" size={18} />}
          </div>
          <div className="text-white/70 text-sm text-start">
            Receive a verification code on your phone for final confirmation.
          </div>
          <SetupStatus isComplete={smsAuth} />
        </button>
      </div>
    </Layout>
  );
};
